import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../core/alert-dialog';
import { buttonVariants } from '../core/button';
import { cn } from '../../utils';

export type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the user confirms. The dialog closes itself afterwards. */
  onConfirm: () => void;
  /** Heading. Defaults to a generic "Please confirm". */
  title?: React.ReactNode;
  /** The question being asked (e.g. the old `window.confirm` message). */
  description?: React.ReactNode;
  confirmLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  /** Style the confirm button as a destructive action. */
  destructive?: boolean;
};

/**
 * Small modal replacement for `window.confirm`, built on `AlertDialog`.
 *
 * Usage: keep the pending item in state, open the dialog when it is set,
 * and run the action in `onConfirm`.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel,
  destructive = false,
}) => {
  const { t } = useTranslation('admin');

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title ?? t('common.confirmDialog.title')}</AlertDialogTitle>
          {description ? (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{cancelLabel ?? t('common.confirmDialog.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            className={cn(destructive && buttonVariants({ variant: 'destructive' }))}
            onClick={onConfirm}
          >
            {confirmLabel ?? t('common.confirmDialog.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
